import { Calendar, Clock, FileText } from 'react-feather'
import { useLocale, useTranslations } from '@/lib/next-shim'

type DashboardHeaderProps = {
  firstName?: string
  consultationsCount: number
  ordersCount: number
  documentsCount: number
}

export function DashboardHeader({ firstName, consultationsCount, ordersCount, documentsCount }: DashboardHeaderProps) {
  const t = useTranslations('page.AccountDashboard')
  const locale = useLocale()

  const today = new Intl.DateTimeFormat(locale || 'en', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date())

  return (
    <header className="relative mx-5 overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-[#013a4d] to-[#015066] p-6 text-white shadow-sm sm:p-8">
      {/* Decorative accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/10" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 right-24 h-36 w-36 rounded-full bg-white/5" />

      <div className="relative flex flex-col gap-5">
        <div className="flex flex-col gap-1.5">
          <p className="text-xs font-medium capitalize text-white/70 sm:text-sm">{today}</p>
          <h1 className="text-2xl font-bold leading-tight sm:text-3xl">
            {t('welcome')}
            {firstName ? `, ${firstName}` : ''}
          </h1>
          <p className="max-w-prose text-sm text-white/80 sm:text-base">{t('subtitle')}</p>
        </div>

        <div className="flex flex-wrap gap-2.5">
          <StatChip icon={<Calendar size={16} aria-hidden="true" />} value={consultationsCount} label={t('stats.consultations')} />
          <StatChip icon={<Clock size={16} aria-hidden="true" />} value={ordersCount} label={t('stats.requests')} />
          <StatChip icon={<FileText size={16} aria-hidden="true" />} value={documentsCount} label={t('stats.documents')} />
        </div>
      </div>
    </header>
  )
}

type StatChipProps = {
  icon: React.ReactNode
  value: number
  label: string
}

function StatChip({ icon, value, label }: StatChipProps) {
  return (
    <div
      className="flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-2 text-sm ring-1 ring-inset ring-white/15 backdrop-blur-sm"
      aria-label={`${value} ${label}`}
    >
      <span aria-hidden="true" className="text-white/80 inline-flex justify-center items-center">
        {icon}
      </span>
      <span aria-hidden="true" className="flex items-baseline gap-1">
        <span className="font-semibold tabular-nums">{value}</span>
        <span className="text-white/75">{label}</span>
      </span>
    </div>
  )
}
